import { useAtomValue } from "jotai";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { logger } from "c7-logger";

import { Text } from "chameleon/lib/components";
import { PaymentMethodTitle } from "chameleon/lib/components/payment-method-title/payment-method-title";
import { RadioGroup } from "chameleon/lib/components/radio-group/radio-group";

import {
  paymentMessagesAtom,
  paymentRequirementsAtom,
} from "checkout-experience/atoms/payment-atoms";
import { FormSection } from "checkout-experience/components/forms/form-section";
import { ApplePay } from "checkout-experience/components/payment-method/pay-options/apple/apple-pay";
import LpmFooter from "checkout-experience/components/payment-method/pay-options/lpm/lpm-footer/lpm-footer";
import PayByBankLinkBankAccountButton from "checkout-experience/components/payment-method/pay-options/pay-by-bank/pay-by-bank-link-bank-account-button";
import { PayByBankPaymentSummary } from "checkout-experience/components/payment-method/pay-options/pay-by-bank/pay-by-bank-payment-summary";
import { containerClass } from "checkout-experience/components/payment-method/payment-methods-list.css";
import { usePaymentOptionContext } from "checkout-experience/components/payment-method/payment-option-provider";
import { useEnabledPaymentMethods } from "checkout-experience/hooks/use-enabled-payment-methods";
import { useIsExpired } from "checkout-experience/hooks/use-is-expired";
import { usePaymentMethodsRadioGroupOptions } from "checkout-experience/hooks/use-payment-methods-radio-group-options";
import {
  useHideSetAsDefault,
  useOnChangeSavedPaymentType,
  useSavedPaymentMethodOptions,
} from "checkout-experience/hooks/use-saved-payment-methods-list";
import { useChexContext } from "checkout-experience/providers/chex-context-wrapper";
import { PaymentDomain } from "checkout-query-client/api/types";
import { usePaymentForm } from "checkout-query-client/hooks/use-form";
import { usePubSub } from "checkout-query-client/hooks/use-pub-sub";
import {
  PubSubMessage,
  PubSubNamespace,
} from "checkout-query-client/publish-subcriber/types";
import { ChexFieldValues } from "checkout-query-client/types/chex-form-fields";
import { PaymentType } from "checkout-query-client/types/form-types";
import { isAltPaymentType } from "checkout-query-client/utils/helpers";

import {
  sendCustomAction,
  sendCustomTiming,
  sendCustomTimingDifference,
} from "datadog-loader/loader";

import { CardSummary, PlainMessage } from "frontend-client";

import { RADIO_GROUP_PAYMENT_METHODS_TYPE } from "./constants";

interface PaymentMethodsListProps {
  paymentDomain?: PaymentDomain;
  setApplePayComponent: (el: JSX.Element | null) => void;
  editSavedPaymentMethod: boolean;
  setEditSavedPaymentMethod: Dispatch<SetStateAction<boolean>>;
  surchargeText?: string;
}

const CombinedPaymentMethodsList = ({
  paymentDomain,
  setApplePayComponent,
  surchargeText,
  editSavedPaymentMethod,
  setEditSavedPaymentMethod,
}: PaymentMethodsListProps) => {
  const [isExpanded, setIsExpanded] = useState<boolean | undefined>(undefined);
  const [isFormSectionDisplayed, setIsFormSectionDisplayed] =
    useState<boolean>(true);
  const [formSectionHeader, setFormSectionHeader] =
    useState<JSX.Element | null>(null);
  const [formSectionFooter, setFormSectionFooter] =
    useState<JSX.Element | null>(null);
  const [showActionSection, setShowActionSection] = useState<boolean>(true);
  const [isPayByBankLinkingInProgress, setIsPayByBankLinkingInProgress] =
    useState<boolean>(false);
  const { paymentOption, setPaymentOption } = usePaymentOptionContext() ?? {};
  const { formId = "", paymentType = PaymentType.creditCard } =
    paymentOption ?? {};
  const { merchantSettings, checkoutSession, checkoutSessionToken } =
    useChexContext();
  const { data: formConfig } = usePaymentForm({
    checkoutSession,
    merchantSettings,
    paymentType,
    formId,
    checkoutSessionToken,
  });
  const [editFormId, setEditFormId] = useState<string | undefined>();
  const [eventSentOneOrMoreScriptsLoaded, setEventSentOneOrMoreScriptsLoaded] =
    useState(false);
  const [eventSentNoPaymentMethodsLoaded, setEventSentNoPaymentMethodsLoaded] =
    useState(false);

  const { setError, watch, setValue } = useFormContext();
  const formFields = watch(`${formId}`) as ChexFieldValues;
  const isExpired = useIsExpired();
  const hideSetAsDefault = useHideSetAsDefault();

  const paymentRequirements = useAtomValue(paymentRequirementsAtom);
  const paymentMessages = useAtomValue(paymentMessagesAtom);
  const isSavedPaymentMethod = (paymentType as string) !== formId;

  const { t } = useTranslation("combined");

  const { radioGroupOptions: newRadioGroupOptions } =
    usePaymentMethodsRadioGroupOptions(surchargeText);

  const {
    paymentMethodsList,
    isPaymentMethodsListInitialized,
    isPaymentMethodsListLoaded,
  } = useEnabledPaymentMethods();

  const walletChanges = (paymentType: PaymentType) => {
    if (paymentType !== PaymentType.applePay) {
      setApplePayComponent(null);
    }

    if (paymentType === PaymentType.applePay) {
      setApplePayComponent(<ApplePay paymentDomain={paymentDomain} />);
      setIsExpanded(false);
    } else if (paymentType === PaymentType.googlePay) {
      setIsExpanded(false);
    } else {
      setIsExpanded(true);
    }
  };

  const payByBankChanges = (paymentType: PaymentType) => {
    if (
      // initial pay by bank block - onChange we're conditionally setting the display of the pay by bank button here (similar to Apple Pay) and outside of 'the normal form submit flow' since the onClick will need to make the first api call without frontend validation for mandates.
      paymentType === PaymentType.payByBank &&
      !paymentRequirements.payByBankConsentUrl &&
      !paymentMessages.payByBankCompletionMessage?.payload?.payResult
        ?.paymentMethod?.payByBank.last4digitsOfAccountNumber &&
      !paymentMessages.payByBankCompletionMessage?.payload?.payResult
        ?.paymentMethod?.payByBank.payByBankRequestId
    ) {
      // show pay by bank link account button - payment method is pay by bank, but no consentUrl and no completion messages -- user needs to do first fake pay call to get one.
      setIsPayByBankLinkingInProgress(false);
      setShowActionSection(false);
      setIsFormSectionDisplayed(false);
      setFormSectionFooter(
        <PayByBankLinkBankAccountButton
          paymentDomain={paymentDomain}
          isPayByBankLinkingInProgress={isPayByBankLinkingInProgress}
          setIsPayByBankLinkingInProgress={setIsPayByBankLinkingInProgress}
        />
      );
    }

    if (
      // user closes popup before finishes, redisplay button as new
      paymentType === PaymentType.payByBank &&
      paymentRequirements.payByBankConsentUrl &&
      !paymentMessages.payByBankCompletionMessage
    ) {
      setIsPayByBankLinkingInProgress(false);
      setShowActionSection(false);
      setIsFormSectionDisplayed(false);
      setFormSectionFooter(
        <PayByBankLinkBankAccountButton
          paymentDomain={paymentDomain}
          isPayByBankLinkingInProgress={isPayByBankLinkingInProgress}
          setIsPayByBankLinkingInProgress={setIsPayByBankLinkingInProgress}
        />
      );
    }

    if (
      // user finishes pay by bank pop up, selects another payment method and then reselects pay by bank
      paymentType === PaymentType.payByBank &&
      !paymentRequirements.payByBankConsentUrl &&
      paymentMessages.payByBankCompletionMessage?.payload?.payResult
        ?.paymentMethod?.payByBank.last4digitsOfAccountNumber &&
      paymentMessages.payByBankCompletionMessage?.payload?.payResult
        .paymentMethod.payByBank.payByBankRequestId
    ) {
      // scenario 3
      const {
        bankName,
        accountType,
        last4digitsOfAccountNumber,
        institutionalLogo,
      } =
        paymentMessages.payByBankCompletionMessage.payload?.payResult
          .paymentMethod.payByBank || {};
      setIsPayByBankLinkingInProgress(false);
      setShowActionSection(true);
      setIsFormSectionDisplayed(true);
      setFormSectionHeader(
        <PayByBankPaymentSummary
          accountType={accountType}
          bankName={bankName}
          last4={last4digitsOfAccountNumber}
          institutionalLogo={institutionalLogo}
        />
      );
      setFormSectionFooter(null);
    }
  };

  const lpmChanges = (paymentType: PaymentType) => {
    if (isAltPaymentType(paymentType)) {
      setShowActionSection(false);
      setFormSectionFooter(<LpmFooter />);
    }
  };

  const onChangeSavedPaymentType = useOnChangeSavedPaymentType(
    editFormId,
    setEditFormId
  );

  const onChangePaymentType = (value: string, type: string) => {
    if (type === RADIO_GROUP_PAYMENT_METHODS_TYPE.newPaymentMethods) {
      // init on every onChange
      setFormSectionHeader(null);
      setFormSectionFooter(null);
      setShowActionSection(true);
      setIsFormSectionDisplayed(true);

      walletChanges(value as PaymentType);
      payByBankChanges(value as PaymentType);
      lpmChanges(value as PaymentType);

      if (setPaymentOption)
        setPaymentOption({
          paymentType: value as PaymentType,
          formId: value,
        });
    } else {
      onChangeSavedPaymentType(value);
    }
  };

  const resetAnimation = useMemo(() => {
    const activeFieldsLength = Object.values(formConfig?.fields || {}).filter(
      (field) => field?.active
    ).length;
    return `${activeFieldsLength}_${formId}`;
  }, [formConfig?.fields, formId]);

  useEffect(() => {
    // This is the useEffect for payByBank payment type. User can either..
    // scenario 1. have no consentURL, no paymentMessages yet -- show button to link account.
    // scenario 2. have consentURL and no paymentMessages - still in process of going through external link account flow. - Show spinner disabled button
    // scenario 3. have no consentURL and good paymentMessages - user is ready to make normal pay call. - Show regular form, clear paymentMessage after
    // scenario 4. have consentURL and bad paymentMessages - user or system had issues with external flow.  (show error banner and reset to try again)
    if (paymentType === PaymentType.payByBank) {
      if (
        !paymentRequirements.payByBankConsentUrl &&
        !paymentMessages.payByBankCompletionMessage
      ) {
        // scenario 1
        setIsPayByBankLinkingInProgress(false);
        setShowActionSection(false);
        setIsFormSectionDisplayed(false);
        setFormSectionHeader(null);
        setFormSectionFooter(
          <PayByBankLinkBankAccountButton
            paymentDomain={paymentDomain}
            isPayByBankLinkingInProgress={false}
            setIsPayByBankLinkingInProgress={setIsPayByBankLinkingInProgress}
          />
        );
      } else if (
        paymentRequirements.payByBankConsentUrl &&
        !paymentMessages.payByBankCompletionMessage
      ) {
        // scenario 2
        setIsPayByBankLinkingInProgress(true);
        setShowActionSection(false);
        setIsFormSectionDisplayed(false);
        setFormSectionHeader(null);
        setFormSectionFooter(
          <PayByBankLinkBankAccountButton
            isPayByBankLinkingInProgress={true}
            paymentDomain={paymentDomain}
            setIsPayByBankLinkingInProgress={setIsPayByBankLinkingInProgress}
          />
        );
      } else if (
        !paymentRequirements.payByBankConsentUrl &&
        paymentMessages.payByBankCompletionMessage?.payload?.payResult
          ?.paymentMethod?.payByBank.last4digitsOfAccountNumber &&
        paymentMessages.payByBankCompletionMessage?.payload?.payResult
          .paymentMethod.payByBank.payByBankRequestId
      ) {
        // scenario 3
        const {
          bankName,
          accountType,
          last4digitsOfAccountNumber,
          institutionalLogo,
        } =
          paymentMessages.payByBankCompletionMessage.payload?.payResult
            .paymentMethod.payByBank || {};
        setShowActionSection(true);
        setIsFormSectionDisplayed(true);
        setFormSectionHeader(
          <PayByBankPaymentSummary
            accountType={accountType}
            bankName={bankName}
            last4digitsOfAccountNumber={last4digitsOfAccountNumber}
            institutionalLogo={institutionalLogo}
          />
        );
        setFormSectionFooter(null);
      } else if (
        paymentRequirements.payByBankConsentUrl &&
        !paymentMessages.payByBankCompletionMessage?.payload?.payResult
          ?.paymentMethod?.payByBank.last4digitsOfAccountNumber &&
        !paymentMessages.payByBankCompletionMessage?.payload?.payResult
          ?.paymentMethod?.payByBank.payByBankRequestId
      ) {
        // scenario 4
        setIsPayByBankLinkingInProgress(false);
        setShowActionSection(false);
        setIsFormSectionDisplayed(false);

        setFormSectionFooter(
          <>
            <Text styleVariant={"body"} colorVariant={"critical"}>
              {t("payByBank.errorLinking")}
            </Text>
            <PayByBankLinkBankAccountButton
              paymentDomain={paymentDomain}
              isPayByBankLinkingInProgress={false}
              setIsPayByBankLinkingInProgress={setIsPayByBankLinkingInProgress}
            />
          </>
        );
      } else {
        // default behavior
        setFormSectionHeader(null);
        setFormSectionFooter(null);
        setShowActionSection(true);
        setIsFormSectionDisplayed(true);
      }
    }

    // LPM
    if (isAltPaymentType(paymentType)) {
      setShowActionSection(false);
      setFormSectionFooter(<LpmFooter />);
    }
  }, [paymentRequirements, paymentMessages, paymentType]);

  const onEditPaymentMethod = (id: string) => {
    setEditSavedPaymentMethod(true);
    setEditFormId(id);
  };

  const onCancelEditPaymentMethod = () => {
    setEditSavedPaymentMethod(false);
    revertEditChanges();
  };

  const revertEditChanges = () => {
    const paymentMethod = checkoutSession?.paymentMethods?.find(
      (p) => p.paymentMethodId === formId
    );
    if (!paymentMethod) return;

    const { expirationMonth, expirationYear } = (paymentMethod.paymentMethod
      .value ?? {}) as PlainMessage<CardSummary>;

    if (formFields.expiration) {
      setValue(
        `${formId}.expiration`,
        `${expirationMonth} / ${expirationYear - 2000}`.padStart(7, "0")
      );
    } else if (formFields.expirationMonth) {
      setValue(`${formId}.expirationMonth`, `${expirationMonth}`);
      setValue(`${formId}.expirationYear`, `${expirationYear}`);
    }

    if (
      formFields.billingAddressName &&
      paymentMethod.billingAddress?.recipients?.length
    ) {
      setValue(
        `${formId}.billingAddressName`,
        `${paymentMethod.billingAddress?.recipients[0]}`
      );
    }

    if (formFields.billingAddressCountry) {
      setValue(
        `${formId}.billingAddressCountry`,
        `${paymentMethod.billingAddress?.regionCode}`
      );
    }

    if (
      formFields.billingAddressAddressLine1 &&
      paymentMethod.billingAddress?.addressLines.length
    ) {
      setValue(
        `${formId}.billingAddressAddressLine1`,
        `${paymentMethod.billingAddress?.addressLines?.[0]}`
      );
    }

    if (
      formFields.billingAddressAddressLine2 &&
      paymentMethod.billingAddress?.addressLines.length &&
      paymentMethod.billingAddress?.addressLines.length > 1
    ) {
      setValue(
        `${formId}.billingAddressAddressLine2`,
        `${paymentMethod.billingAddress?.addressLines?.[2]}`
      );
    }

    if (formFields.billingAddressCity) {
      setValue(
        `${formId}.billingAddressCity`,
        `${paymentMethod.billingAddress?.locality}`
      );
    }
    if (formFields.billingAddressAdministrativeArea) {
      setValue(
        `${formId}.billingAddressAdministrativeArea`,
        `${paymentMethod.billingAddress?.administrativeArea}`
      );
    }

    if (formFields.billingAddressZip) {
      setValue(
        `${formId}.billingAddressZip`,
        `${paymentMethod.billingAddress?.postalCode}`
      );
    }
  };

  useEffect(() => {
    /** if selected saved MOP is expired, we are registering error in form state
     * But this is a note that field is not registered as form is not loaded
     */
    if (isExpired) {
      setError(`${formId}.expiration`, { type: "isFutureExpiration" });
    }
  }, [isExpired, formId]);

  useEffect(() => {
    if (editFormId && !editSavedPaymentMethod) {
      setEditFormId(undefined);
    }
  }, [editFormId, editSavedPaymentMethod, hideSetAsDefault]);

  // Log to datadog once the radio groups are defined
  useEffect(() => {
    if (newRadioGroupOptions.length > 0) {
      // run once this component is rendered
      sendCustomTiming({ name: "chexRenderPaymentMethodList" });
      sendCustomTimingDifference({
        startName: "chexMountStart",
        endName: "chexRenderPaymentMethodListDiff",
      });
    }
  }, [newRadioGroupOptions.length > 0]);

  /**
   * Form is always visible if security code is required for pay
   * otherwise if edit mode is selected
   */
  const showForm = useMemo(
    () =>
      Boolean(
        (paymentType === PaymentType.creditCard &&
          merchantSettings?.paymentMethods?.cardPaymentMethod
            ?.captureSecurityCodeOnPayWithProfile) ||
          editFormId === formId
      ),
    [paymentType, merchantSettings, formId, editFormId]
  );

  const savedRadioGroupOptions = useSavedPaymentMethodOptions(
    editFormId ?? "",
    onCancelEditPaymentMethod,
    onEditPaymentMethod,
    surchargeText
  );

  const radioGroupOptions = useMemo(
    () => [...savedRadioGroupOptions, ...newRadioGroupOptions],
    [savedRadioGroupOptions, newRadioGroupOptions]
  );

  useEffect(() => {
    // This useEffect() is for pubsub events
    if (
      isPaymentMethodsListInitialized &&
      paymentMethodsList.length > 0 &&
      paymentMethodsList.every((mop) => mop.isLoading === false) &&
      paymentMethodsList.every((mop) => mop.active === false) &&
      radioGroupOptions.length === 0 &&
      !eventSentNoPaymentMethodsLoaded
    ) {
      logger.error("FailedToLoad - No payment methods loaded");
      sendCustomAction({
        name: "FailedToLoad",
        context: { reason: "No payment methods loaded" },
      });
      usePubSub()?.publish({
        level: "info",
        namespace: PubSubNamespace.render,
        message: PubSubMessage.FailedToLoad,
      });
      setEventSentNoPaymentMethodsLoaded(true);
    }

    if (
      isPaymentMethodsListInitialized &&
      paymentMethodsList.every((mop) => mop.isLoading === false) &&
      paymentMethodsList.some((mop) => mop.isLoaded === true) &&
      radioGroupOptions.length > 0 &&
      !eventSentOneOrMoreScriptsLoaded
    ) {
      logger.log("Ready - The form has loaded at least one payment method.");
      sendCustomAction({
        name: "Ready",
        context: { reason: "The form has loaded at least one payment method" },
      });
      usePubSub()?.publish({
        level: "info",
        namespace: PubSubNamespace.render,
        message: PubSubMessage.Ready,
      });
      setEventSentOneOrMoreScriptsLoaded(true);
    }
  }, [paymentMethodsList, radioGroupOptions]);

  useEffect(() => {
    if (
      savedRadioGroupOptions?.length === 0 &&
      isPaymentMethodsListLoaded &&
      paymentMethodsList.length === 1 &&
      paymentOption?.paymentType !== paymentMethodsList[0].name
    ) {
      // no saved payment methods, paymentMethodsList[] are all loaded and only one payment type, fire the onChange manually to set up the things
      onChangePaymentType(
        paymentMethodsList[0].name,
        RADIO_GROUP_PAYMENT_METHODS_TYPE.newPaymentMethods
      );
    }
  }, [isPaymentMethodsListLoaded, savedRadioGroupOptions]);

  // TODO: Turn `isPaymentMethodsListLoading` from useEnabledPaymenMethods() into a spinner
  // console.log('isPaymentMethodsListLoading', isPaymentMethodsListLoading);

  return (
    <>
      {radioGroupOptions.length > 1 ? (
        <RadioGroup
          options={radioGroupOptions}
          selectedOption={formId}
          tabIndex={0}
          name="paymentMethod"
          onChange={onChangePaymentType}
          resetAnimation={resetAnimation}
          isExpanded={isSavedPaymentMethod ? showForm : isExpanded}
          dividerIndex={
            savedRadioGroupOptions.length === 0
              ? -1
              : savedRadioGroupOptions.length
          }
          dividerText={
            savedRadioGroupOptions.length
              ? (t("savedPaymentMethod.moreWaysToPay") ?? "")
              : ""
          }
        >
          <>
            {!isSavedPaymentMethod ? formSectionHeader : null}
            {(isSavedPaymentMethod && showForm) ||
            (!isSavedPaymentMethod && isFormSectionDisplayed) ? (
              <FormSection
                showActionSection={
                  isSavedPaymentMethod ? !!editFormId : showActionSection
                }
                editSavedPaymentMethod={
                  isSavedPaymentMethod ? editSavedPaymentMethod : undefined
                }
              />
            ) : (
              <></>
            )}
            {!isSavedPaymentMethod ? formSectionFooter : null}
          </>
        </RadioGroup>
      ) : radioGroupOptions[0] ? (
        <div className={containerClass}>
          <PaymentMethodTitle
            forId={radioGroupOptions[0].id}
            title={radioGroupOptions[0].label}
            leadingSlot={radioGroupOptions[0].leadingSlot}
            trailingSlot={radioGroupOptions[0].trailingSlot}
          />
          {surchargeText && radioGroupOptions[0].id === formId ? (
            <Text
              styleVariant="body"
              tagElement="span"
              data-testid={"surcharge-text-disclaimer"}
            >
              {surchargeText}
            </Text>
          ) : null}
          <div>
            {formSectionHeader}
            {isFormSectionDisplayed ? (
              <FormSection showActionSection={showActionSection} />
            ) : (
              <></>
            )}
            {formSectionFooter}
          </div>
        </div>
      ) : null}
    </>
  );
};
export default CombinedPaymentMethodsList;
